import { useMemo } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type ClipType } from '@air/api/types';

import { useWorkspaceAgentSelectAssetStore } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { type WorkspaceAgentSelectAssetResults } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { usePrivateWorkspaceAgentBoardTree } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/usePrivateWorkspaceAgentBoardTree';
import { useWorkspaceAgentMentionChildPrefetch } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionChildPrefetch';
import { EMPTY_WORKSPACE_AGENT_MENTION_ITEMS } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import { type PrivateWorkspaceAgentBoardAssetSearchFilters } from '~/components/v2/components/workspace/workspace-agent-panel/queries/getPrivateWorkspaceAgentBoardAssetSearchFilters';
import { PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentAssetMentionSearch';
import { usePrivateWorkspaceAgentBoardAssetSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentBoardAssetSearch';
import { usePrivateWorkspaceAgentBrandKitMentionSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentBrandKitMentionSearch';

const EMPTY_NODE_IDS: ReadonlySet<string> = new Set();

const noop = () => undefined;

export type UsePrivateWorkspaceAgentSelectAssetDropdownParams = {
  clipTypes?: readonly ClipType[];
  filters?: PrivateWorkspaceAgentBoardAssetSearchFilters;
  workspaceId: string | undefined;
};

export const usePrivateWorkspaceAgentSelectAssetDropdown = ({
  clipTypes = PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES,
  filters,
  workspaceId,
}: UsePrivateWorkspaceAgentSelectAssetDropdownParams) => {
  const isOpen = useWorkspaceAgentSelectAssetStore((state) => state.isOpen);
  const source = useWorkspaceAgentSelectAssetStore((state) => state.source);
  const isBoardPickerOpen = useWorkspaceAgentSelectAssetStore((state) => state.isBoardPickerOpen);
  const query = useWorkspaceAgentSelectAssetStore((state) => state.query);
  const boardPickerQuery = useWorkspaceAgentSelectAssetStore((state) => state.boardPickerQuery);
  const selectedBoard = useWorkspaceAgentSelectAssetStore((state) => state.selectedBoard);
  const setSource = useWorkspaceAgentSelectAssetStore((state) => state.setSource);
  const boardHighlightedIndex = useWorkspaceAgentSelectAssetStore((state) => state.boardHighlightedIndex);

  const isBrandKitEnabled = isOpen && source === 'brand-kit' && !isBoardPickerOpen;
  const isBoardAssetsEnabled = isOpen && source === 'board' && !isBoardPickerOpen;
  const isBoardTreeEnabled = isOpen && isBoardPickerOpen;

  const brandKitSearch = usePrivateWorkspaceAgentBrandKitMentionSearch({
    enabled: isBrandKitEnabled,
    isPickerOpen: isOpen,
    query: '',
    workspaceId: workspaceId ?? '',
  });

  const boardTree = usePrivateWorkspaceAgentBoardTree({
    enabled: isBoardTreeEnabled,
    isPickerOpen: isOpen,
    query: boardPickerQuery,
    workspaceId,
  });

  const boardAssets = usePrivateWorkspaceAgentBoardAssetSearch({
    boardId: selectedBoard?.id,
    enabled: isBoardAssetsEnabled,
    filters,
    query,
    types: clipTypes,
    workspaceId,
  });

  useIsomorphicLayoutEffect(() => {
    if (source === 'brand-kit' && brandKitSearch.hasLooked && brandKitSearch.brandKits.length === 0) {
      setSource('board');
    }
  }, [brandKitSearch.brandKits.length, brandKitSearch.hasLooked, setSource, source]);

  const assetItems = source === 'brand-kit' ? brandKitSearch.items : boardAssets.items;
  const { prefetchSpec } = useWorkspaceAgentMentionChildPrefetch({
    highlightedIndex: boardHighlightedIndex,
    items: boardTree.items,
    listQuerySpecs: boardTree.listQuerySpecs,
    onToggleExpanded: boardTree.toggleExpanded,
    query: boardPickerQuery,
    workspaceId,
  });

  const boardListQuerySpecs =
    prefetchSpec && boardTree.listQuerySpecs.every((spec) => spec.nodeId !== prefetchSpec.nodeId)
      ? [...boardTree.listQuerySpecs, prefetchSpec]
      : boardTree.listQuerySpecs;

  const activeSearch = source === 'brand-kit' ? brandKitSearch : boardAssets;

  const assetResults = useMemo(
    (): WorkspaceAgentSelectAssetResults => ({
      hasNextPage: source === 'brand-kit' ? false : boardAssets.hasNextPage,
      hasNextPageNodeIds: EMPTY_NODE_IDS,
      isError: activeSearch.isError,
      isFetching: activeSearch.isFetching,
      isLoading: activeSearch.isLoading,
      isStale: activeSearch.isStale,
      items: isBoardPickerOpen ? EMPTY_WORKSPACE_AGENT_MENTION_ITEMS : assetItems,
      onFetchNextPage: activeSearch.fetchNextPage,
      onFetchNextPageForNode: noop,
      onRetry: activeSearch.refetch,
    }),
    [
      activeSearch.fetchNextPage,
      activeSearch.isError,
      activeSearch.isFetching,
      activeSearch.isLoading,
      activeSearch.isStale,
      activeSearch.refetch,
      assetItems,
      boardAssets.hasNextPage,
      isBoardPickerOpen,
      source,
    ],
  );

  const boardResults = useMemo(
    (): WorkspaceAgentSelectAssetResults => ({
      hasNextPage: false,
      hasNextPageNodeIds: boardTree.hasNextPageNodeIds,
      isError: boardTree.isError,
      isFetching: boardTree.isFetching,
      isLoading: boardTree.isLoading,
      isStale: boardTree.isStale,
      items: boardTree.items,
      onFetchNextPage: noop,
      onFetchNextPageForNode: boardTree.fetchNextPageForNode,
      onRetry: boardTree.refetch,
      onToggleExpanded: boardTree.toggleExpanded,
      pendingChildCountNodeIds: boardTree.pendingChildCountNodeIds ?? EMPTY_NODE_IDS,
    }),
    [
      boardTree.fetchNextPageForNode,
      boardTree.hasNextPageNodeIds,
      boardTree.isError,
      boardTree.isFetching,
      boardTree.isLoading,
      boardTree.isStale,
      boardTree.items,
      boardTree.pendingChildCountNodeIds,
      boardTree.refetch,
      boardTree.toggleExpanded,
    ],
  );

  return useMemo(
    () => ({
      assetResults,
      boardListQuerySpecs,
      boardResults,
      brandKits: brandKitSearch.brandKits,
      onListSnapshot: boardTree.onListSnapshot,
      onSelectBrandKit: brandKitSearch.selectBrandKit,
      selectedBrandKitId: brandKitSearch.selectedBrandKitId,
    }),
    [
      assetResults,
      boardListQuerySpecs,
      boardResults,
      boardTree.onListSnapshot,
      brandKitSearch.brandKits,
      brandKitSearch.selectBrandKit,
      brandKitSearch.selectedBrandKitId,
    ],
  );
};
